<template>
  <!-- Managing reminders -->
  <div
    class="flex flex-col gap-6 w-full h-fit bg-white border border-grays-400 rounded py-6 pl-4 pr-9"
  >
    <div class="flex flex-col md:flex-row w-full h-fit md:items-center gap-3">
      <div class="flex flex-row items-center w-full">
        <h3 class="flex-1 text-xl font-medium text-primary-700 min-h-9">
          ניהול תזכורות
        </h3>
        <button-icon v-if="!editMode" @click="toggleEditMode" class="md:hidden">edit</button-icon>
      </div>
      <div class="flex flex-row items-center gap-4">
        <span class="text-wrap text-grays-700 text-xs font-medium">
          אנחנו משתדלים לתת לכם תזכורת. אבל החובה לזכור ולעקוב אחרי הזמנים
          השונים, היא שלכם
        </span>
        <button-icon v-if="!editMode" @click="toggleEditMode" class="hidden md:block">edit</button-icon>
      </div>
    </div>
    <div class="flex flex-row gap-9 items-end">
      <div class="xl:flex flex-row gap-20">
        <!-- whenToRemind -->
        <div class="md:flex flex-col gap-4">
          <p class="text-grays-900 font-medium text-lg">מתי לשלוח תזכורת?</p>
          <div class="md:flex flex-row gap-4 relative">
            <check-box
              v-for="remind in whenToRemind"
              :key="remind.type"
              :disabled="!editMode"
              :model-value="remind.value"
              @update:model-value="updateWhenToRemind(remind.type, $event)"
            >
              {{ remind.display }}
            </check-box>
            <div
              v-if="phoneRequiredMessage"
              class="text-sm text-error absolute bottom-[-40px] right-2"
            >
              {{ phoneRequiredMessage }}
              <span class="text-error material-symbols-rounded text-sm"
                >error</span
              >
            </div>
          </div>
        </div>
        <!-- whereToRemind -->
        <div
          v-if="atLeastOneWhenToRemindSelected()"
          class="flex flex-col gap-4 mt-0.5"
        >
          <div class="mt-3 md:mt-0 md:flex flex-row items-center gap-2">
            <p class="text-grays-900 font-medium text-lg">
              איפה תרצה לקבל תזכורת?
            </p>
            <span class="text-grays-700 text-sm">מינימום בחירה 1 - חובה</span>
          </div>
          <div class="flex flex-col gap-2">
            <!-- Checkboxes in a row -->
            <div class="flex flex-row items-center gap-[90px]">
              <check-box
                v-for="remind in whereToRemind.slice().reverse()"
                :key="remind.type"
                :disabled="!editMode"
                :model-value="remind.value"
                @update:model-value="updateWhereToRemind(remind.type, $event)"
              >
                {{ remind.display }}
              </check-box>
            </div>
            <div class="md:flex flex-row gap-2">
              <div class="min-w-[150px] max-w-[150px] flex items-center">
                <p class="text-grays-500 text-sm font-regular">
                  {{ profileData.email }}
                </p>
              </div>
              <!-- Dropdown menu -->
              <div v-if="editMode">
                <dropdown-reminders
                  :disabled="
                    !whereToRemind.find((remind) => remind.type === 'phone')
                      ?.value
                  "
                  :phones="allPhoneNumbers"
                  :remind-all-phones="allPhoneNumbersToRemind"
                  :selected-phones="selectedPhoneNumbers"
                  @update-phones-reminds="updatePhonesReminds"
                  @update-all-phone-numbers-to-remind="
                    updateAllPhoneNumbersToRemind
                  "
                />
              </div>
              <div
                v-else-if="
                  whereToRemind.find((remind) => remind.type === 'phone')?.value
                "
                class="text-grays-500 text-sm font-regular"
              >
                {{
                  allPhoneNumbersToRemind
                    ? "All numbers"
                    : selectedPhoneNumbers.join(", ")
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Save changes -->
    <div v-if="editMode" class="flex flex-col items-end">
      <button-secondary @click="handleSave">לעדכן פרטים</button-secondary>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import ButtonIcon from "@/components/Global/ButtonIcon.vue";
import CheckBox from "@/components/Global/CheckBox.vue";
import DropdownReminders from "@/components/Profile/DropdownReminders.vue";
import ButtonSecondary from "@/components/Global/ButtonSecondary.vue";
import axiosInstance from "@/helpers/axios";

export default {
  name: "ManagingRemindersCard",
  components: { ButtonSecondary, DropdownReminders, CheckBox, ButtonIcon },
  props: {
    profileData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editMode: false,
      selectedPhoneNumbers: [],
      allPhoneNumbersToRemind: false,
      phoneRequiredMessage: "",
    };
  },
  methods: {
    ...mapMutations({
      setWhenToRemind: "user/setWhenToRemind",
      setWhereToRemind: "user/setWhereToRemind",
      updateAllPhoneNumbers: "user/updateAllPhoneNumbers",
      setPhoneRemind: "user/setPhoneRemind",
    }),
    completeData() {
      this.setWhenToRemind({
        type: "beforeClosingSale",
        value: this.profileData.remind_before_closing_sale,
      });
      this.setWhenToRemind({
        type: "dayOfPickup",
        value: this.profileData.remind_on_day_of_pickup,
      });
      this.setWhenToRemind({
        type: "openingNewSale",
        value: this.profileData.remind_on_opening_new_sale,
      });

      this.updateAllPhoneNumbers(
        this.profileData.phones.map((phone) => ({
          tel: phone.tel,
          no_msg: phone.no_msg,
        }))
      );

      this.selectedPhoneNumbers = this.profileData.phones
        .filter((phone) => phone.no_msg === 0)
        .map((phone) => phone.tel);
      if (this.selectedPhoneNumbers.length > 0) {
        this.setWhereToRemind({ type: "phone", value: 1 });
      }
      this.setWhereToRemind({
        type: "email",
        value: this.profileData.email_notifications,
      });
    },
    atLeastOneWhenToRemindSelected() {
      return this.whenToRemind.some((remind) => remind.value);
    },
    toggleEditMode() {
      this.editMode = !this.editMode;
    },
    validateReminders() {
      this.phoneRequiredMessage = "";

      const isWhenToRemindSelected = this.atLeastOneWhenToRemindSelected();
      const hasWhereToRemind = this.whereToRemind.some(
        (remind) => remind.value
      );
      const hasPhoneReminder = this.whereToRemind.some(
        (remind) => remind.type === "phone" && remind.value
      );

      if (isWhenToRemindSelected && hasWhereToRemind) {
        if (hasPhoneReminder && !this.selectedPhoneNumbers.length) {
          this.phoneRequiredMessage = "Choose at least one phone number";
          return false;
        }
        return true;
      }

      if (isWhenToRemindSelected && !hasWhereToRemind) {
        this.phoneRequiredMessage = "Choose where to remind";
        return false;
      }

      return true;
    },
    async handleSave() {
      const valid = this.validateReminders();
      try {
        if (valid) {
          this.phoneRequiredMessage = "";
          const whenToRemind = this.whenToRemind
            .map((remind) => {
              if (remind.value === 1) {
                if (remind.type === "beforeClosingSale") {
                  return "remind_before_closing_sale";
                } else if (remind.type === "openingNewSale") {
                  return "remind_on_opening_new_sale";
                } else if (remind.type === "dayOfPickup") {
                  return "remind_on_day_of_pickup";
                }
              }
            })
            .filter((remind) => remind !== undefined);
          const whereToRemind = [
            {
              type: "email",
              value: +this.whereToRemind.some(
                (remind) => remind.type === "email" && remind.value === 1
              ),
            },
            ...(this.whereToRemind.find((item) => item.type === "phone")
              ? this.allPhoneNumbersToRemind
                ? [{ type: "phone", value: "all" }]
                : this.selectedPhoneNumbers.map((phone) => ({
                    type: "phone",
                    value: +phone,
                  }))
              : []),
          ];

          await axiosInstance.put("/clients/notifications", {
            whereToRemind,
            whenToRemind,
          });
          if (!this.whereToRemind.find((item) => item.type === "phone")) {
            this.selectedPhoneNumbers = [];
            this.allPhoneNumbers.map((phone, index) => {
              this.setPhoneRemind({ index, value: 1 });
            });
          }
          this.toggleEditMode();
        }
      } catch (e) {
        console.error(e);
      }
    },
    updateWhenToRemind(type, value) {
      this.setWhenToRemind({ type, value });
    },
    updateWhereToRemind(type, value) {
      this.setWhereToRemind({ type, value });
      if (!this.whereToRemind.find((remind) => remind.value === 1)) {
        this.setWhereToRemind({ type, value: 1 });
      }
    },
    updatePhonesReminds(data) {
      const { tel } = data;
      if (this.selectedPhoneNumbers.indexOf(tel) !== -1) {
        this.selectedPhoneNumbers = this.selectedPhoneNumbers.filter(
          (phone) => +phone !== +tel
        );
      } else {
        this.selectedPhoneNumbers.push(tel);
      }
    },
    updateAllPhoneNumbersToRemind(newValue) {
      this.updateAllPhoneNumbers(
        this.allPhoneNumbers.map((phone) => {
          return { ...phone, no_msg: newValue };
        })
      );
      this.selectedPhoneNumbers = !newValue
        ? this.allPhoneNumbers.map((phone) => phone.tel)
        : [];
    },
  },
  computed: {
    ...mapGetters({
      whenToRemind: "user/getWhenToRemind",
      whereToRemind: "user/getWhereToRemind",
      allPhoneNumbers: "user/getAllPhoneNumbers",
    }),
  },
  watch: {
    profileData() {
      this.completeData();
    },
  },
};
</script>


<style>
/* Add any necessary styles here */
</style>
